import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    Lightning,
    Trash,
    UsersThree,
    CheckCircle,
} from "@phosphor-icons/react";

/**
 * AdminDemoDataPanel — one-click demo seeding for production deploys.
 *
 * Wraps two admin endpoints into a single CTA so the user doesn't have to
 * crack open DevTools or Postman post-deploy:
 *
 *   1. POST /api/admin/seed-demo                 — 2 doctors · 35 patients ·
 *                                                  35 cases · compliance data ·
 *                                                  appointments
 *   2. POST /api/admin/backfill-patient-avatars  — assigns randomuser.me
 *                                                  portraits to any patient
 *                                                  still missing one
 *
 * Both endpoints are idempotent — running twice produces the same final
 * state. A small "Wipe" affordance is included for the case where the user
 * wants the cohort gone before a real customer session.
 */
export default function AdminDemoDataPanel() {
    const [busy, setBusy] = useState(false);
    const [wiping, setWiping] = useState(false);
    const [result, setResult] = useState(null);

    async function runSeed() {
        setBusy(true);
        setResult(null);
        try {
            // Explicit empty body + Content-Type so proxies/CDNs that strip
            // the Authorization header on "bodyless" POSTs (some configs of
            // CloudFront/Nginx do this) treat the call as a normal JSON POST.
            const seed = await api.post("/admin/seed-demo", {}, {
                headers: { "Content-Type": "application/json" },
                timeout: 60000,
            });
            // Run backfill in series so legacy patients (Emma Carter, soft-launch
            // fixtures) also pick up faces. seed-demo touches only its own tagged
            // cohort, backfill mops up the rest.
            const backfill = await api.post("/admin/backfill-patient-avatars", {}, {
                headers: { "Content-Type": "application/json" },
                timeout: 60000,
            });
            setResult({
                counts: seed.data?.counts || {},
                doctors: seed.data?.doctors || [],
                backfill: backfill.data || {},
                elapsed_ms: seed.data?.elapsed_ms,
            });
            toast.success("Demo data seeded", {
                description: `${seed.data?.counts?.patients || 0} patients · ${seed.data?.counts?.cases || 0} cases · ${backfill.data?.updated || 0} avatars backfilled`,
            });
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Seed failed";
            toast.error("Seeding failed", { description: msg });
            setResult({ error: msg });
        } finally {
            setBusy(false);
        }
    }

    async function runWipe() {
        if (!window.confirm("This will delete every record tagged by the demo seed (doctors, patients, cases, wear logs, check-ins, appointments). Continue?")) {
            return;
        }
        setWiping(true);
        try {
            const r = await api.delete("/admin/seed-demo", {
                headers: { "Content-Type": "application/json" },
                timeout: 60000,
            });
            toast.success("Demo cohort wiped", {
                description: Object.entries(r.data?.wiped || {})
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(" · "),
            });
            setResult(null);
        } catch (e) {
            toast.error("Wipe failed", { description: e?.response?.data?.detail || e?.message });
        } finally {
            setWiping(false);
        }
    }

    return (
        <div
            className="mt-6 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-transparent p-5"
            data-testid="admin-demo-data-panel"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <Lightning size={18} weight="fill" className="text-primary" />
                        <h3 className="font-display text-lg font-semibold">Demo data</h3>
                        <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5">
                            Admin only
                        </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Seeds 2 demo doctors (Dr. Ananya Mehra + Dr. Arjun Verma), 35 patients with photos, 35 cases with compliance data, and 14 appointments. Then backfills portrait avatars for any remaining patient without one. Idempotent — safe to re-run.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        onClick={runSeed}
                        disabled={busy || wiping}
                        className="rounded-full"
                        data-testid="admin-seed-demo-btn"
                    >
                        {busy ? (
                            <>
                                <span className="inline-block w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin mr-2" />
                                Seeding…
                            </>
                        ) : (
                            <>
                                <UsersThree size={16} weight="duotone" className="mr-2" />
                                Seed demo data
                            </>
                        )}
                    </Button>
                    <Button
                        onClick={runWipe}
                        disabled={busy || wiping}
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-muted-foreground hover:text-rose-600 hover:bg-rose-50"
                        data-testid="admin-wipe-demo-btn"
                        title="Delete every record tagged by the demo seed"
                    >
                        <Trash size={14} className="mr-1.5" />
                        {wiping ? "Wiping…" : "Wipe"}
                    </Button>
                </div>
            </div>

            {result?.counts && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="admin-seed-demo-result">
                    <ResultStat label="Cases" value={result.counts.cases} />
                    <ResultStat label="In treatment" value={result.counts.in_treatment} accent="text-emerald-700" />
                    <ResultStat label="Patients" value={result.counts.patients} />
                    <ResultStat label="Wear logs" value={result.counts.wear_logs} />
                    <ResultStat label="Check-ins" value={result.counts.check_ins} />
                    <ResultStat label="Appointments" value={result.counts.appointments} />
                    <ResultStat label="Avatars backfilled" value={result.backfill?.updated ?? 0} accent="text-primary" />
                </div>
            )}

            {result?.doctors?.length > 0 && (
                <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" />
                    <span>
                        Logins ready:{" "}
                        {result.doctors.map((d, i) => (
                            <span key={d.phone}>
                                <span className="font-medium text-foreground">{d.name}</span> · <code className="text-[10px] bg-secondary px-1 py-0.5 rounded">{d.phone}</code> · OTP <code className="text-[10px] bg-secondary px-1 py-0.5 rounded">{d.otp}</code>
                                {i < result.doctors.length - 1 ? "    " : ""}
                            </span>
                        ))}
                    </span>
                </div>
            )}

            {result?.error && (
                <div className="mt-3 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="admin-seed-demo-error">
                    {result.error}
                </div>
            )}
        </div>
    );
}

function ResultStat({ label, value, accent }) {
    return (
        <div className="rounded-lg bg-white/70 border border-border/60 px-3 py-2">
            <div className={`font-display text-xl font-bold leading-tight ${accent || ""}`}>{value ?? 0}</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium mt-0.5">{label}</div>
        </div>
    );
}
